import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircle, faCircleInfo, faSpinner, faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { LocaleOption, PublishJobDto, PublishJobProgress, PublishJobSummary } from '@course-authoring/shared-types';
import { useTheme } from '@course-authoring/shared-types';
import { useLanguageContext } from '../../hooks/useLanguageContext';
import { isPublishJobConflict, retryPublishJob } from '../../api/questionApi';
import { PublishJobProgressBar } from '@course-authoring/ui-blocks';
import { PublishJobDetailsPanel, PublishJobStatusChip } from '@course-authoring/ui-blocks';
import type { PublishJobPopoverPlacement } from '@course-authoring/ui-blocks';
import { deriveCurrentPublishJobProgress, summarizePublishJobLanguageStep } from '@course-authoring/ui-blocks';
import {
  isActiveLessonQuestionPreparation,
  isFullLessonQuestionPreparation,
  questionPreparationKeys,
  useLessonQuestionPreparation,
  type LessonQuestionPreparationData,
} from './useLessonQuestionPreparation';

interface LessonQuestionPreparationStatusProps {
  questionId: number;
}

type PreparationStep = 'translation' | 'narration';
const POPOVER_MAX_WIDTH = 400;
const POPOVER_MAX_HEIGHT = 420;
const POPOVER_GAP = 4;
const VIEWPORT_MARGIN = 8;

function failureDetail(job: PublishJobDto): { step: string | null; locale?: string; code?: string; message?: string } {
  const failedOutcome = job.stepResults.find((outcome) => outcome.status === 'failed');
  const attempt = job.result.attempts.find((item) => item.attempt === job.attempt);
  return {
    step: failedOutcome?.step ?? attempt?.failedStep ?? job.currentStep,
    locale: failedOutcome?.locale,
    code: failedOutcome?.code ?? attempt?.code,
    message: failedOutcome?.message ?? attempt?.message,
  };
}

function statusLabel(job: PublishJobSummary, ready: boolean): string {
  if (ready) return 'Ready';
  if (job.status === 'queued') return 'Queued';
  if (job.status === 'running') {
    return job.currentStep === 'translation' ? 'Translating'
      : job.currentStep === 'narration' ? 'Generating audio' : 'Preparing';
  }
  if (job.status === 'failed') {
    const step = isFullLessonQuestionPreparation(job) ? failureDetail(job).step : job.currentStep;
    return step === 'translation' ? 'Translation failed'
      : step === 'narration' ? 'Audio failed' : 'Preparation failed';
  }
  return 'Ready';
}

function currentProgress(job: LessonQuestionPreparationData | undefined): PublishJobProgress | null {
  if (!job || job.status !== 'running') return null;
  if (isFullLessonQuestionPreparation(job)) return deriveCurrentPublishJobProgress(job);
  return job.progress ?? null;
}

function stepList(job: PublishJobDto): PreparationStep[] {
  const scope = typeof job.scope === 'object' && job.scope !== null
    ? job.scope as { kind?: unknown }
    : {};
  return scope.kind === 'translation' ? ['narration'] : ['translation', 'narration'];
}

function localeLabel(locale: string | undefined, options: LocaleOption[]): string | undefined {
  return locale ? options.find((option) => option.locale === locale)?.label ?? locale : undefined;
}

function stateTitle(job: PublishJobSummary, languageOptions: LocaleOption[]): string {
  if (job.status === 'failed') return 'Preparation stopped';
  if (isFullLessonQuestionPreparation(job)) {
    const scope = typeof job.scope === 'object' && job.scope !== null
      ? job.scope as { kind?: unknown; locale?: unknown }
      : {};
    if (scope.kind === 'translation') {
      const locale = typeof scope.locale === 'string' ? localeLabel(scope.locale, languageOptions) : undefined;
      if (job.status === 'completed') return locale ? `Audio narration is ready for ${locale}` : 'Audio narration is ready';
      return locale ? `Preparing audio narration for ${locale}` : 'Preparing audio narration';
    }
    if (job.status === 'completed') return 'Translations and audio are ready';
    return 'Preparing translations and audio';
  }
  if (job.status === 'completed') return 'Preparation complete';
  return 'Preparing question';
}

function timeLabel(value: string | null, prefix: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${prefix} ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

function jobMeta(job: PublishJobSummary): string {
  const parts: string[] = [];
  if (isFullLessonQuestionPreparation(job)) {
    if (job.status === 'queued') parts.push(timeLabel(job.createdAt, 'Saved') ?? 'Waiting to start');
    else if (job.status === 'running') parts.push(timeLabel(job.startedAt, 'Started') ?? 'In progress');
    else if (job.status === 'failed') parts.push(timeLabel(job.finishedAt, 'Failed') ?? 'Stopped');
    else parts.push(timeLabel(job.finishedAt, 'Finished') ?? 'Finished');
  } else {
    parts.push('Loading detailed results');
  }
  parts.push(`Attempt ${job.attempt}`);
  return parts.join(' · ');
}

function technicalFailureTitle(
  step: string | null,
  locale: string | undefined,
  languageOptions: LocaleOption[],
): string {
  const language = localeLabel(locale, languageOptions);
  if (language && step === 'translation') return `${language} couldn't be translated`;
  if (language && step === 'narration') return `Audio could not be prepared for ${language}`;
  return step === 'translation' ? 'Translation failed'
    : step === 'narration' ? 'Audio narration failed' : 'Preparation failed';
}

function stepIcon(
  stepFailed: boolean,
  done: boolean,
  running: boolean,
  tokens: ReturnType<typeof useTheme>['tokens'],
): React.ReactElement {
  const color = stepFailed ? tokens.ui.errorText : done ? tokens.ui.workflowApproved : running ? tokens.ui.fixedAccent : tokens.text.muted;
  const background = stepFailed ? tokens.ui.errorBackground : done ? `color-mix(in srgb, ${tokens.ui.workflowApproved} 9%, ${tokens.backgrounds.page})`
    : running ? `color-mix(in srgb, ${tokens.ui.fixedAccent} 9%, ${tokens.backgrounds.page})` : tokens.backgrounds.page;
  const border = stepFailed ? tokens.ui.errorBorder : done ? tokens.ui.workflowApproved : running ? tokens.ui.focusRing : tokens.borders.default;
  const icon = stepFailed ? faXmark : done ? faCheck : running ? faSpinner : faCircle;
  return <span aria-label={stepFailed ? 'Step failed' : done ? 'Step complete' : running ? 'Step in progress' : 'Step pending'} style={{ width: 24, height: 24, borderRadius: 12, border: `1px solid ${border}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background, color }}>
    <FontAwesomeIcon icon={icon} spin={running} aria-hidden="true" style={{ fontSize: running ? 12 : stepFailed ? 10 : done ? 11 : 7 }} />
  </span>;
}

function FullPreparationDetails({ job, languageOptions, tokens }: {
  job: PublishJobDto;
  languageOptions: LocaleOption[];
  tokens: ReturnType<typeof useTheme>['tokens'];
}): React.ReactElement {
  const steps = stepList(job);
  const failure = job.status === 'failed' ? failureDetail(job) : null;
  const currentStepIndex = steps.indexOf(job.currentStep as PreparationStep);
  const footnote = job.status === 'failed'
    ? 'Retry restarts preparation for this question. Outputs that remain valid may be reused.'
    : isActiveLessonQuestionPreparation(job)
      ? 'You can keep editing this lesson while this runs.'
      : 'This job finished while the lesson was open.';
  return (
    <>
      {failure && <div role="alert" style={{ margin: `${tokens.spacing.sm} ${tokens.spacing.md} 0`, padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, borderRadius: tokens.radius.md, background: tokens.ui.errorBackground, border: `1px solid ${tokens.ui.errorBorder}` }}>
        <strong style={{ color: tokens.ui.errorText }}>{technicalFailureTitle(failure.step, failure.locale, languageOptions)}</strong>
        <div style={{ marginTop: tokens.spacing.xs, color: tokens.text.primary }}>The question remains selected in this lesson. Retry restarts preparation from the beginning and may reuse outputs that remain valid.</div>
        {(failure.code || failure.message) && <details style={{ marginTop: tokens.spacing.xs, fontSize: 12 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Technical details</summary>
          <div style={{ marginTop: tokens.spacing.xs, fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
            {[failure.code, failure.message].filter(Boolean).join(' · ')}
          </div>
        </details>}
      </div>}
      <div style={{ padding: `0 ${tokens.spacing.md}` }}>
        {steps.map((step) => {
          const summary = summarizePublishJobLanguageStep(step, job.stepResults, languageOptions, job.result.targets?.[step]);
          const running = isActiveLessonQuestionPreparation(job) && job.currentStep === step;
          const pending = isActiveLessonQuestionPreparation(job) && (job.status === 'queued'
            || (currentStepIndex >= 0 && steps.indexOf(step) > currentStepIndex));
          const passed = isActiveLessonQuestionPreparation(job)
            && currentStepIndex >= 0 && steps.indexOf(step) < currentStepIndex;
          const stepFailed = job.stepResults.some((outcome) => outcome.step === step && outcome.status === 'failed')
            || (job.status === 'failed' && failure?.step === step);
          const done = job.status === 'completed' || passed
            || (!running && !pending && summary.summary !== 'No outcomes recorded yet' && !stepFailed);
          const label = step === 'translation' ? 'Translation' : 'Audio narration';
          const summaryText = running
            ? summary.progress ? `${summary.progress.completed} of ${summary.progress.total} languages` : 'Preparing languages'
            : pending ? 'Waiting to start'
              : isActiveLessonQuestionPreparation(job) && currentStepIndex < 0 ? 'Waiting for the current step to be reported'
                : summary.summary;
          const statusColor = stepFailed ? tokens.ui.errorText : done ? tokens.ui.workflowApproved : running ? tokens.ui.fixedAccent : tokens.text.muted;
          return <article key={step} style={{ display: 'flex', alignItems: 'flex-start', gap: tokens.spacing.sm, padding: `${tokens.spacing.sm} 0`, borderBottom: `1px solid ${tokens.borders.default}` }}>
            {stepIcon(stepFailed, done, running, tokens)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong>{label}</strong>
              <div style={{ color: tokens.text.muted }}>{summaryText}</div>
              {running && summary.progress && <PublishJobProgressBar unit="languages" stepLabel={label} progress={summary.progress} />}
              {summary.noVoiceSentence && <div style={{ marginTop: tokens.spacing.xs, fontSize: 12, color: tokens.text.muted }}>{summary.noVoiceSentence}</div>}
              {summary.otherSkipSentences.map((sentence) => <div key={sentence} style={{ marginTop: tokens.spacing.xs, fontSize: 12, color: tokens.text.muted }}>{sentence}</div>)}
              {stepFailed && <span style={{ color: statusColor, fontSize: 12 }}>Failed</span>}
            </div>
          </article>;
        })}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: tokens.spacing.xs, padding: `${tokens.spacing.sm} 0`, color: tokens.text.muted, fontSize: 12, lineHeight: 1.4 }}>
          <FontAwesomeIcon icon={faCircleInfo} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{footnote}</span>
        </div>
      </div>
    </>
  );
}

function preparationPopover(
  job: Exclude<LessonQuestionPreparationData, null>,
  languageOptions: LocaleOption[],
  isFetching: boolean,
  hasReadError: boolean,
  placement: PublishJobPopoverPlacement,
  popoverId: string,
  popoverRef: React.RefObject<HTMLElement | null>,
  popoverElementRef: React.RefCallback<HTMLElement>,
  onClose: () => void,
  tokens: ReturnType<typeof useTheme>['tokens'],
): React.ReactElement {
  return (
    <PublishJobDetailsPanel title={stateTitle(job, languageOptions)} subtitle={jobMeta(job)} placement={placement}
      panelRef={popoverElementRef} onClose={onClose} popoverId={popoverId} busy={!isFullLessonQuestionPreparation(job) && isFetching}>
      {isFullLessonQuestionPreparation(job) ? (
        <FullPreparationDetails job={job} languageOptions={languageOptions} tokens={tokens} />
      ) : (
        <div style={{ padding: tokens.spacing.md, color: tokens.text.muted }}>
          {job.status === 'running' && job.currentStep && <div>
            {job.currentStep === 'translation' ? 'Translation' : job.currentStep === 'narration' ? 'Audio narration' : 'Preparation'}: {statusLabel(job, false)}
            {currentProgress(job) && <><div style={{ marginTop: tokens.spacing.xs }}>{currentProgress(job)!.completed} of {currentProgress(job)!.total} languages</div><PublishJobProgressBar unit="languages" stepLabel={job.currentStep} progress={currentProgress(job)!} /></>}
          </div>}
          <div role="status" style={{ marginTop: tokens.spacing.xs }}>{isFetching ? 'Loading detailed results…' : hasReadError ? 'Detailed results are unavailable; the last known status is retained.' : 'Detailed results are loading…'}</div>
          {isActiveLessonQuestionPreparation(job) && <div style={{ display: 'flex', alignItems: 'flex-start', gap: tokens.spacing.xs, marginTop: tokens.spacing.md, color: tokens.text.muted, fontSize: 12, lineHeight: 1.4 }}>
            <FontAwesomeIcon icon={faCircleInfo} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
            <span>You can keep editing this lesson while this runs.</span>
          </div>}
        </div>
      )}
    </PublishJobDetailsPanel>
  );
}

export function LessonQuestionPreparationStatus({ questionId }: LessonQuestionPreparationStatusProps): React.ReactElement | null {
  const { tokens } = useTheme();
  const { languageOptions } = useLanguageContext();
  const queryClient = useQueryClient();
  const statusQuery = useLessonQuestionPreparation(questionId);
  const job = statusQuery.data;
  const statusGroupRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLElement | null>(null);
  const popoverElementRef = useCallback((node: HTMLElement | null) => {
    popoverRef.current = node;
    node?.focus();
  }, []);
  const statusButtonRef = useRef<HTMLButtonElement>(null);
  const selectionRef = useRef<{ questionId: number; generation: number; activeJobIdentity: string | null }>({
    questionId,
    generation: 0,
    activeJobIdentity: null,
  });
  if (selectionRef.current.questionId !== questionId) {
    selectionRef.current = {
      questionId,
      generation: selectionRef.current.generation + 1,
      activeJobIdentity: null,
    };
  }
  if (job && isActiveLessonQuestionPreparation(job)) {
    selectionRef.current.activeJobIdentity = `${job.id}:${job.attempt}`;
  }
  const jobIdentity = job ? `${job.id}:${job.attempt}` : null;
  const isReady = job?.status === 'completed' && selectionRef.current.activeJobIdentity === jobIdentity;
  const visibleJob = job && (isActiveLessonQuestionPreparation(job) || job.status === 'failed' || isReady) ? job : null;
  const generation = selectionRef.current.generation;
  const [placement, setPlacement] = useState<PublishJobPopoverPlacement | null>(null);
  const [openGeneration, setOpenGeneration] = useState<number | null>(null);
  const [retryingJob, setRetryingJob] = useState<number | null>(null);
  const [retryWarning, setRetryWarning] = useState<{ questionId: number; jobId: number; attempt: number; message: string } | null>(null);
  const popoverOpen = openGeneration === generation && visibleJob !== null;
  const popoverId = `lesson-question-preparation-${useId()}`;
  const warningText = retryWarning?.questionId === questionId
    && retryWarning.jobId === visibleJob?.id
    && retryWarning.attempt === visibleJob?.attempt
    ? retryWarning.message
    : null;
  const hasWarning = statusQuery.isError || Boolean(warningText);
  const hasVisibleContent = visibleJob !== null || (statusQuery.isPending && !job) || hasWarning;

  useLayoutEffect(() => {
    if (!popoverOpen) return undefined;
    const updatePlacement = () => {
      const anchor = statusButtonRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const width = Math.min(POPOVER_MAX_WIDTH, Math.max(0, window.innerWidth - VIEWPORT_MARGIN * 2));
      const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
      const left = Math.min(Math.max(VIEWPORT_MARGIN, rect.left), maxLeft);
      const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - VIEWPORT_MARGIN;
      if (spaceBelow >= Math.min(POPOVER_MAX_HEIGHT, 260) || spaceBelow >= spaceAbove) {
        setPlacement({ left, width, top: rect.bottom + POPOVER_GAP, maxHeight: Math.min(POPOVER_MAX_HEIGHT, Math.max(120, spaceBelow)) });
      } else {
        setPlacement({ left, width, bottom: window.innerHeight - rect.top + POPOVER_GAP, maxHeight: Math.min(POPOVER_MAX_HEIGHT, Math.max(120, spaceAbove)) });
      }
    };
    updatePlacement();
    window.addEventListener('scroll', updatePlacement, true);
    window.addEventListener('resize', updatePlacement);
    return () => {
      window.removeEventListener('scroll', updatePlacement, true);
      window.removeEventListener('resize', updatePlacement);
    };
  }, [popoverOpen, visibleJob?.id, visibleJob?.attempt, visibleJob?.status, currentProgress(visibleJob ?? undefined)?.completed]);

  useEffect(() => {
    if (!popoverOpen) return undefined;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!statusGroupRef.current?.contains(target) && !popoverRef.current?.contains(target)) setOpenGeneration(null);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setOpenGeneration(null);
      statusButtonRef.current?.focus();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [popoverOpen]);

  if (!hasVisibleContent) return null;

  const progress = currentProgress(visibleJob ?? undefined);
  const label = visibleJob ? statusLabel(visibleJob, isReady) : null;
  const accessibleButtonText = label && progress
    ? `Preparation status: ${label}, ${progress.completed} of ${progress.total} languages`
    : label ? `Preparation status: ${label}` : 'Preparation status details';
  const retrying = visibleJob ? retryingJob === visibleJob.id : false;
  const rootStyle: CSSProperties = {
    display: 'inline-flex',
    flex: hasWarning ? '0 1 100%' : '0 1 auto',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    maxWidth: '100%',
    minWidth: 0,
  };
  const warningStyle: CSSProperties = {
    display: 'flex',
    flex: '0 1 100%',
    alignItems: 'flex-start',
    gap: tokens.spacing.xs,
    minWidth: 0,
    marginTop: tokens.spacing.xs,
    padding: tokens.spacing.xs,
    borderRadius: tokens.radius.sm,
    border: `1px solid color-mix(in srgb, ${tokens.ui.workflowDraft} 45%, ${tokens.backgrounds.page})`,
    background: `color-mix(in srgb, ${tokens.ui.workflowDraft} 9%, ${tokens.backgrounds.page})`,
    color: tokens.text.primary,
    fontSize: 12,
  };
  const onRetry = async () => {
    if (!visibleJob || visibleJob.status !== 'failed') return;
    const jobToRetry = visibleJob;
    setRetryingJob(jobToRetry.id);
    setRetryWarning(null);
    try {
      const updatedJob = await retryPublishJob(jobToRetry.id);
      await queryClient.cancelQueries({ queryKey: questionPreparationKeys.detail(questionId), exact: true });
      queryClient.setQueryData(questionPreparationKeys.detail(questionId), updatedJob);
    } catch (error) {
      if (isPublishJobConflict(error)) {
        await queryClient.cancelQueries({ queryKey: questionPreparationKeys.detail(questionId), exact: true });
        if (error.publishJob !== undefined) {
          queryClient.setQueryData(questionPreparationKeys.detail(questionId), error.publishJob);
        }
        await queryClient.invalidateQueries({ queryKey: questionPreparationKeys.detail(questionId), exact: true });
      } else {
        setRetryWarning({
          questionId,
          jobId: jobToRetry.id,
          attempt: jobToRetry.attempt,
          message: 'Could not restart preparation. The last known status is still shown; try again shortly.',
        });
      }
    } finally {
      setRetryingJob((current) => current === jobToRetry.id ? null : current);
    }
  };

  return (
    <div ref={statusGroupRef} aria-live="polite" style={rootStyle}>
      <span aria-hidden="true" style={{ width: 1, height: 14, flexShrink: 0, background: tokens.borders.default }} />
      {statusQuery.isPending && !job ? (
        <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: tokens.spacing.xs, color: tokens.text.muted, fontSize: 12, whiteSpace: 'nowrap' }}>
          <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />Checking status…
        </span>
      ) : visibleJob && label ? (
        <>
          <PublishJobStatusChip
            triggerRef={statusButtonRef}
            state={visibleJob.status}
            label={label}
            accessibleLabel={accessibleButtonText}
            progress={progress ? `${progress.completed} of ${progress.total}` : null}
            expanded={popoverOpen}
            controls={popoverId}
            onClick={() => setOpenGeneration(popoverOpen ? null : generation)}
            onRetry={visibleJob.status === 'failed' ? () => { void onRetry(); } : undefined}
            retryLabel="Retry preparation for this question"
            retryTitle="Retry preparation from the beginning"
            retrying={retrying}
          />
        </>
      ) : null}
      {hasWarning && <div role="alert" style={warningStyle}>
        <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" style={{ color: tokens.ui.workflowDraft, marginTop: 1, flexShrink: 0 }} />
        <span>{statusQuery.isError
          ? job
            ? "Couldn't refresh preparation status. Showing the last known state; it will update once the connection recovers."
            : "Couldn't check preparation status. It will update once the connection recovers."
          : warningText}</span>
      </div>}
      {popoverOpen && visibleJob && placement && preparationPopover(
        visibleJob,
        languageOptions,
        statusQuery.isFetching,
        statusQuery.isError,
        placement,
        popoverId,
        popoverRef,
        popoverElementRef,
        () => { setOpenGeneration(null); statusButtonRef.current?.focus(); },
        tokens,
      )}
    </div>
  );
}
